// pages/User/components/UserAdsCard.jsx
import { Link } from 'react-router-dom';
import { BiTimeFive } from 'react-icons/bi';
import { MdLocationOn } from 'react-icons/md';
import { AiOutlineEye } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { convertCurrency } from '@/utils/currencyConverter';
// import errorImage from '@/utils/errorImage';
import formatDate from '@/helpers/formatDate';
import logo from '@/assets/rentasvet_main_black_transparent_logo.png';
import getLanguageVersion from '@/utils/getLanguageVersion';

const UserAdsCard = ({ ad, viewMode = 'grid' }) => {
  const { t, i18n } = useTranslation();
  const { currency, currencySymbol } = useSelector((state) => state.currencyStore);

  const getImageUrl = () => {
    if (ad.images && ad.images.length > 0 && ad.images[0]) {
      return `${import.meta.env.VITE_IMAGE_URL}${ad.images[0]}`;
    }
    return logo;
  };

  const getPriceDisplay = () => {
    if (ad.askForPrice) {
      return <span className='text-lg font-bold text-primary-yellow'>{t('askForPrice')}</span>;
    }

    if (ad.priceFrom === ad.priceUpTo && (ad.priceFrom !== 0 || ad.priceUpTo !== 0)) {
      return (
        <span className='text-lg font-bold text-primary-yellow'>
          {convertCurrency(ad.priceFrom, currency)} {currencySymbol}
        </span>
      );
    }

    if (ad.priceFrom || ad.priceUpTo) {
      return (
        <span className='text-lg font-bold text-primary-yellow'>
          {ad.priceFrom ? convertCurrency(ad.priceFrom, currency) : ''}
          {ad.priceUpTo ? `${ad.priceFrom ? ' - ' : ''}${convertCurrency(ad.priceUpTo, currency)}` : ''} {currencySymbol}
        </span>
      );
    }

    return <span className='text-sm text-gray-500'>{t('priceNotSpecified')}</span>;
  };

  if (viewMode === 'list') {
    return (
      <Link
        to={`/products/details/${ad._id}`}
        className='group flex flex-col gap-4 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-md transition-all duration-300 hover:shadow-2xl sm:flex-row'
      >
        {/* Image */}
        <div className='relative h-48 w-full flex-shrink-0 overflow-hidden rounded-lg bg-gray-200 sm:h-32 sm:w-48'>
          <img
            src={getImageUrl()}
            alt={getLanguageVersion(i18n.language, ad.title, ad.title_en)}
            className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-110'
            onError={(e) => {
              e.target.src = logo;
              e.target.className = e.target.className.replace('object-cover', 'object-contain');
            }}
          />
          {ad.adType && ad.adType !== 'REGULAR' && (
            <div className='absolute left-2 top-2 rounded-full bg-primary-yellow px-3 py-1 text-xs font-bold text-gray-800 shadow-md'>
              {t(ad.adType)}
            </div>
          )}
          {ad.isPaused && (
            <div className='absolute right-2 top-2 rounded-full bg-red-500 px-3 py-1 text-xs font-bold text-white shadow-md'>
              {t('paused')}
            </div>
          )}
        </div>

        {/* Content */}
        <div className='flex flex-grow flex-col justify-between'>
          <div>
            <div className='mb-2 flex items-start justify-between gap-2'>
              <h3 className='line-clamp-2 text-xl font-bold text-gray-800 transition-colors group-hover:text-primary-yellow'>
                {getLanguageVersion(i18n.language, ad.title, ad.title_en)}
              </h3>
            </div>

            {ad.category && ad.category.length > 0 && (
              <p className='mb-2 flex items-center gap-1 text-sm text-gray-500'>
                <span className='h-2 w-2 rounded-full bg-primary-yellow'></span>
                {t(ad.category[0].name)}
              </p>
            )}

            <div className='mb-3'>{getPriceDisplay()}</div>
          </div>

          <div className='flex flex-wrap items-center gap-4 text-sm text-gray-600'>
            <span className='flex items-center gap-1'>
              <MdLocationOn className='text-primary-yellow' />
              {ad.city}
            </span>
            <span className='flex items-center gap-1'>
              <BiTimeFive className='text-primary-yellow' />
              {formatDate(ad.createAt)}
            </span>
            <span className='flex items-center gap-1'>
              <AiOutlineEye className='text-primary-yellow' />
              {ad.views || 0} {t('views')}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  // Grid View (default)
  return (
    <Link
      to={`/products/details/${ad._id}`}
      className='group block overflow-hidden rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:shadow-2xl'
    >
      {/* Image */}
      <div className='relative h-56 overflow-hidden bg-gray-200'>
        <img
          src={getImageUrl()}
          alt={getLanguageVersion(i18n.language, ad.title, ad.title_en)}
          className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-110'
          onError={(e) => {
            e.target.src = logo;
            e.target.className = e.target.className.replace('object-cover', 'object-contain');
          }}
        />

        {/* Badges */}
        <div className='absolute left-3 right-3 top-3 flex items-start justify-between'>
          {ad.adType && ad.adType !== 'REGULAR' && (
            <div className='rounded-full bg-primary-yellow px-3 py-1.5 text-xs font-bold text-gray-800 shadow-lg backdrop-blur-sm'>
              {t(ad.adType)}
            </div>
          )}
          {ad.isPaused && (
            <div className='ml-auto rounded-full bg-red-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg backdrop-blur-sm'>
              {t('paused')}
            </div>
          )}
        </div>

        {/* Hover Overlay */}
        <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100'></div>
      </div>

      {/* Content */}
      <div className='p-5'>
        {/* Title */}
        <h3 className='mb-2 line-clamp-2 min-h-[3.5rem] text-lg font-bold text-gray-800 transition-colors group-hover:text-primary-yellow'>
          {getLanguageVersion(i18n.language, ad.title, ad.title_en)}
        </h3>

        {/* Category */}
        {ad.category && ad.category.length > 0 && (
          <div className='mb-3 flex items-center gap-2'>
            <span className='h-2 w-2 rounded-full bg-primary-yellow'></span>
            <p className='text-sm text-gray-600'>{t(ad.category[0].name)}</p>
          </div>
        )}

        {/* Price */}
        <div className='mb-4 border-b border-t border-gray-100 py-2'>{getPriceDisplay()}</div>

        {/* Footer Info */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between text-sm'>
            <span className='flex items-center gap-1.5 text-gray-600'>
              <MdLocationOn className='text-base text-primary-yellow' />
              <span className='font-medium'>{ad.city}</span>
            </span>
            <span className='flex items-center gap-1.5 text-gray-600'>
              <AiOutlineEye className='text-base text-primary-yellow' />
              <span>{ad.views || 0}</span>
            </span>
          </div>

          <div className='flex items-center gap-1.5 text-xs text-gray-500'>
            <BiTimeFive className='text-primary-yellow' />
            <span>{formatDate(ad.createAt)}</span>
          </div>
        </div>
      </div>

      {/* Hover Effect Border */}
      <div className='h-1 origin-left scale-x-0 transform bg-gradient-to-r from-primary-yellow via-yellow-400 to-primary-yellow transition-transform duration-300 group-hover:scale-x-100'></div>
    </Link>
  );
};

export default UserAdsCard;
